/**
 * OwnerDistributionPanel - Top-level orchestrator for the VOC distribution system
 *
 * Replaces OwnerRewardsPanel when the project uses the new distribution config.
 * Tabs: Settings, Compounds, Swaps, Revenue, Exclusions, Overrides, Royalties, Periods
 */

import React, { useState } from 'react';
import { Tabs } from '../../ui/components/Tabs';
import { FeatherGift } from '@subframe/core';
import { RewardsSettingsForm } from './RewardsSettingsForm';
import { CompoundEntryForm } from './CompoundEntryForm';
import { SwapEntryForm } from './SwapEntryForm';
import { RevenueEntryForm } from './RevenueEntryForm';
import { WalletExclusionManager } from './WalletExclusionManager';
import { RewardAddressOverrideManager } from './RewardAddressOverrideManager';
import { RoyaltyDashboard } from './RoyaltyDashboard';
import { PeriodManager } from './PeriodManager';

interface OwnerDistributionPanelProps {
  projectSlug: string;
  projectCollections: Array<{ id: string; name: string }>;
}

export const OwnerDistributionPanel: React.FC<OwnerDistributionPanelProps> = ({
  projectSlug,
  projectCollections,
}) => {
  const [activeTab, setActiveTab] = useState('periods');

  return (
    <div className="flex w-full flex-col gap-6">
      <div className="flex w-full items-center gap-2">
        <FeatherGift className="h-5 w-5 text-green-600" />
        <span className="text-heading-3 font-heading-3 text-default-font">
          Distribution Management
        </span>
      </div>

      <Tabs>
        <Tabs.Item active={activeTab === 'periods'} onClick={() => setActiveTab('periods')}>
          Periods
        </Tabs.Item>
        <Tabs.Item active={activeTab === 'compounds'} onClick={() => setActiveTab('compounds')}>
          Compounds
        </Tabs.Item>
        <Tabs.Item active={activeTab === 'swaps'} onClick={() => setActiveTab('swaps')}>
          Swaps
        </Tabs.Item>
        <Tabs.Item active={activeTab === 'revenue'} onClick={() => setActiveTab('revenue')}>
          Revenue
        </Tabs.Item>
        <Tabs.Item active={activeTab === 'royalties'} onClick={() => setActiveTab('royalties')}>
          Royalties
        </Tabs.Item>
        <Tabs.Item active={activeTab === 'exclusions'} onClick={() => setActiveTab('exclusions')}>
          Exclusions
        </Tabs.Item>
        <Tabs.Item active={activeTab === 'overrides'} onClick={() => setActiveTab('overrides')}>
          Overrides
        </Tabs.Item>
        <Tabs.Item active={activeTab === 'settings'} onClick={() => setActiveTab('settings')}>
          Settings
        </Tabs.Item>
      </Tabs>

      <div className="mt-2">
        {activeTab === 'periods' && (
          <PeriodManager projectSlug={projectSlug} />
        )}
        {activeTab === 'compounds' && (
          <CompoundEntryForm projectSlug={projectSlug} />
        )}
        {activeTab === 'swaps' && (
          <SwapEntryForm projectSlug={projectSlug} />
        )}
        {activeTab === 'revenue' && (
          <RevenueEntryForm projectSlug={projectSlug} />
        )}
        {activeTab === 'royalties' && (
          <RoyaltyDashboard projectSlug={projectSlug} />
        )}
        {activeTab === 'exclusions' && (
          <WalletExclusionManager projectSlug={projectSlug} />
        )}
        {activeTab === 'overrides' && (
          <RewardAddressOverrideManager projectSlug={projectSlug} />
        )}
        {activeTab === 'settings' && (
          <RewardsSettingsForm projectSlug={projectSlug} projectCollections={projectCollections} />
        )}
      </div>
    </div>
  );
};
